import { useState } from "react";
import { useForm } from "react-hook-form";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useFleetVehicles } from "@/hooks/use-fleet-entities";

interface TireInstallationSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInstall: (data: any) => void;
  tire: any;
  companyId: string;
}

export function TireInstallationSheet({ 
  open, 
  onOpenChange,
  onInstall, 
  tire,
  companyId 
}: TireInstallationSheetProps) {
  const { data: vehicles = [] } = useFleetVehicles(companyId);
  const { register, handleSubmit, setValue, watch } = useForm();
  
  const selectedVehicleId = watch('vehicleId');

  const handleVehicleChange = (vehicleId: string) => {
    setValue('vehicleId', vehicleId);
    
    // Buscar o veículo selecionado e preencher o odômetro
    const selectedVehicle = vehicles.find(v => v.id === vehicleId);
    if (selectedVehicle?.odometer) {
      setValue('vehicleKm', selectedVehicle.odometer);
    }
  };

  const onSubmit = (data: any) => {
    onInstall({
      tireId: tire.id,
      ...data
    });
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[450px] p-0 overflow-hidden flex flex-col">
        <SheetHeader>
          <SheetTitle>Instalar Pneu</SheetTitle>
        </SheetHeader>

        <ScrollArea className="flex-1 -mx-6 px-6">
          <form id="tire-installation-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">
            <div>
              <Label>Pneu</Label>
              <Input 
                value={`${tire?.brand} ${tire?.model} - ${tire?.serial_number}`} 
                disabled 
              />
            </div>

            <div>
              <Label>Veículo *</Label>
              <Select onValueChange={handleVehicleChange} required>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o veículo" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map((vehicle) => (
                    <SelectItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.plate} - {vehicle.make} {vehicle.model}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Posição *</Label>
              <Select onValueChange={(v) => setValue('position', v)} required>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a posição" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dianteira_esquerda">Dianteira Esquerda</SelectItem>
                  <SelectItem value="dianteira_direita">Dianteira Direita</SelectItem>
                  <SelectItem value="traseira_esquerda">Traseira Esquerda</SelectItem>
                  <SelectItem value="traseira_direita">Traseira Direita</SelectItem>
                  <SelectItem value="estepe">Estepe</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </form>
        </ScrollArea>

        <SheetFooter className="border-t pt-4 mt-auto">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="submit" form="tire-installation-form">
            Instalar
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
